// pagination.component.ts
import { Component, EventEmitter, Input, Output, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ButtonColors, ButtonModule } from '@ed---interne/ng-uui-components';
// TODO: adapter ce chemin :

type PageToken = number | '...';

@Component({
    selector: 'table-pagination',
    standalone: true,
    imports: [CommonModule, ButtonModule],
    templateUrl: './table-pagination.component.html',
    styleUrls: ['./table-pagination.component.scss'],
})
export class TablePaginationComponent {
    // --- Inputs
    @Input({ required: true })
    totalPages: number = 1;

    @Input({ required: true })
    currentPage = 1;

    /**
     * Nombre de « pages voisines » autour de la page courante.
     * Ex: 1 → montre [current-1, current, current+1]
     */
    @Input() siblingCount = 5;

    /** Nombre max de boutons page affichés (hors < >). */
    @Input() maxButtons = 7;

    // --- Output
    @Output() pageChange = new EventEmitter<number>();

    // Expose l'enum au template
    ButtonColors = ButtonColors;

    // Pages calculées (avec ellipses)
    pages = computed<PageToken[]>(() =>
        buildWindowedPages(this.totalPages, this.currentPage, this.siblingCount, this.maxButtons),
    );

    // --- Actions
    goTo(page: number) {
        const p = clamp(page, 1, this.totalPages);
        if (p !== this.currentPage) this.pageChange.emit(p);
    }

    goPrev() {
        this.goTo(this.currentPage - 1);
    }
    goNext() {
        this.goTo(this.currentPage + 1);
    }
}

/** Construit une pagination compacte avec ellipses. */
function buildWindowedPages(
    total: number,
    current: number,
    siblingCount = 1,
    maxButtons = 7,
): PageToken[] {
    total = Math.max(1, Math.floor(total));
    current = clamp(Math.floor(current), 1, total);
    siblingCount = Math.max(0, Math.floor(siblingCount));
    maxButtons = Math.max(5, Math.floor(maxButtons)); // 5 min: 1 … c … … last

    // Petit total → toutes les pages
    if (total <= maxButtons) return range(1, total);

    const first = 1;
    const last = total;
    const start = Math.max(first + 1, current - siblingCount);
    const end = Math.min(last - 1, current + siblingCount);

    const showLeftEllipsis = start > first + 1;
    const showRightEllipsis = end < last - 1;

    const middle = range(start, end);
    const pages: PageToken[] = [first];

    if (showLeftEllipsis) pages.push('...');
    pages.push(...middle);
    if (showRightEllipsis) pages.push('...');

    pages.push(last);

    // Si on dépasse maxButtons (cas limites), on resserre autour de current
    if (pages.filter((p) => p !== '...').length > maxButtons) {
        return squeezeAroundCurrent(total, current, maxButtons);
    }
    return pages;
}

function squeezeAroundCurrent(total: number, current: number, maxButtons: number): PageToken[] {
    const half = Math.floor((maxButtons - 3) / 2); // en gardant first, last et deux ellipses
    let start = Math.max(2, current - half);
    let end = Math.min(total - 1, current + half);

    // Ajustements si près des bords
    if (current - start < half) end = Math.min(total - 1, start + 2 * half);
    if (end - current < half) start = Math.max(2, end - 2 * half);

    const res: PageToken[] = [1];
    if (start > 2) res.push('...');
    res.push(...range(start, end));
    if (end < total - 1) res.push('...');
    res.push(total);
    return res;
}

function clamp(n: number, min: number, max: number) {
    return Math.min(max, Math.max(min, n));
}
function range(a: number, b: number): number[] {
    const out: number[] = [];
    for (let i = a; i <= b; i++) out.push(i);
    return out;
}
